// Modals.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';

import { profileSelectors } from '../../store/selectors/profile';
import { authSelectors } from '../../store/selectors/auth';
import { setBirthDateThunk, setTelegramThunk, saveBirthDateThunk } from '../../store/thunks/profile';
import ModalInfo from './ModalInfo';
import usePreviewFeature from '../../hooks/usePreviewFeature';

const HOMEWORK_MODAL_SHOWN_AT = 'homeworkModalShownAt';
const LAST_LESSON_HOMEWORK_MODAL_SHOWN_AT = 'lastLessonHomeworkModalShownAt';

// --- Розклад показу модалки телеграм-боту ---------------------------------
// Раніше модалка перевідкривалась на кожен фоновий рефетч профілю (~кожні 30с),
// бо не було памʼяті про попередній показ. Тепер тримаємо в localStorage час
// останнього показу + лічильник і показуємо лише коли минув потрібний інтервал.
// Перший показ — одразу; далі паузи ескалюються: 1 → 3 → 10 → 30 → 30… днів,
// і 30 днів повторюється, поки студент не приєднає бот. Максимум 1 показ/день.
const TELEGRAM_MODAL_SHOWN_AT = 'telegramModalShownAt';
const TELEGRAM_MODAL_SHOW_COUNT = 'telegramModalShowCount';
const DAY_MS = 24 * 60 * 60 * 1000;
// Пауза (у днях) ПЕРЕД наступним показом залежно від кількості вже зроблених показів.
// index 0 → перший показ (одразу); далі 1, 3, 10, 30; за межами масиву — тримаємо 30.
const TELEGRAM_MODAL_GAP_DAYS = [0, 1, 3, 10, 30];

const shouldShowTelegramModal = () => {
  const rawShownAt = localStorage.getItem(TELEGRAM_MODAL_SHOWN_AT);
  const count = parseInt(localStorage.getItem(TELEGRAM_MODAL_SHOW_COUNT), 10) || 0;

  // Ще жодного разу не показували — показуємо одразу.
  // Перевіряємо саме наявність ключа, а не правдивість значення
  // (щоб коректно обробити граничний збережений час 0).
  if (rawShownAt === null) return true;

  const shownAt = parseInt(rawShownAt, 10);
  if (Number.isNaN(shownAt)) return true;

  const gapIndex = Math.min(count, TELEGRAM_MODAL_GAP_DAYS.length - 1);
  const gapMs = TELEGRAM_MODAL_GAP_DAYS[gapIndex] * DAY_MS;

  return Date.now() - shownAt >= gapMs;
};

const markTelegramModalShown = () => {
  const count = parseInt(localStorage.getItem(TELEGRAM_MODAL_SHOW_COUNT), 10) || 0;
  localStorage.setItem(TELEGRAM_MODAL_SHOWN_AT, Date.now().toString());
  localStorage.setItem(TELEGRAM_MODAL_SHOW_COUNT, (count + 1).toString());
};

const resetTelegramModalSchedule = () => {
  localStorage.removeItem(TELEGRAM_MODAL_SHOWN_AT);
  localStorage.removeItem(TELEGRAM_MODAL_SHOW_COUNT);
};

const Modals = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [birthday, setBirthday] = useState('');
  const [birthDateError, setBirthDateError] = useState('');
  const [birthDateLoading, setBirthDateLoading] = useState(false);

  const [openModalTelegram, setOpenModalTelegram] = useState(false);
  const [openModalBirth, setOpenModalBirth] = useState(false);
  const [openModalHomework, setOpenModalHomework] = useState(false);
  const [openLastLessonModalHomework, setOpenLastLessonModalHomework] = useState(false);
  const [openModalLoginAttempt, setOpenModalLoginAttempt] = useState(false);

  const profile = useSelector(profileSelectors);
  const auth = useSelector(authSelectors);

  const token = auth.token;
  const user = profile.user || {};
  const telegramMessage = profile.telegram || {};
  const birthDateMessage = profile.birthDate || {};

  const isRedesign = usePreviewFeature('courses-redesign');
  const withoutImage = (notification) => (isRedesign ? notification : { ...notification, image: '' });
  const isShowLoginAttempt = !!user.is_show_login_attempt_message
    && (isRedesign || Boolean(user.title_login_attempt_message || user.text_login_attempt_message));

  const loginAttemptNotification = {
    title: user.title_login_attempt_message,
    text: user.text_login_attempt_message,
    button: user.button_login_attempt_message,
    image: isRedesign ? user.image_login_attempt_message : '',
  };
  const lastLessonModalHomework = {
    title: user.last_lesson_homework_title,
    text: user.last_lesson_homework_text,
    button: user.last_lesson_homework_button,
  };

  const telegram = user.available_link_message_register_telegram;
  const birthDate = user.available_notification_filling_birth_date;

  const setHomeworkPage = () => {
    localStorage.setItem('eventId', JSON.stringify(user.last_event_id));
    localStorage.setItem('realHomeworkId', JSON.stringify(user.last_homework_id));
    navigate('/homework', { state: { from: location.pathname } });
  };

  const saveBirthDate = async () => {
    setBirthDateError('');

    if (!birthday) {
      setBirthDateError('Будь ласка, вкажіть дату народження');
      return;
    }

    setBirthDateLoading(true);

    try {
      await dispatch(saveBirthDateThunk({
        token,
        birth_date: birthday,
      })).unwrap();

      setOpenModalBirth(false);
    } catch (error) {
      setBirthDateError('Не вдалося зберегти дату народження. Спробуйте ще раз.');
    } finally {
      setBirthDateLoading(false);
    }
  };

  useEffect(() => {
    if (Object.keys(telegramMessage).length > 0) {
      // Є повідомлення (студент ще не приєднав бот) — показуємо за розкладом.
      if (shouldShowTelegramModal()) {
        setOpenModalTelegram(true);
        markTelegramModalShown();
      }
    } else if (Object.keys(user).length > 0 && !telegram) {
      // Профіль завантажено і прапор явно вимкнено (студент приєднав бот) —
      // скидаємо розклад, щоб при повторній появі потреби він стартував з нуля.
      resetTelegramModalSchedule();
    }

    if (Object.keys(birthDateMessage).length > 0) setOpenModalBirth(true);

    if (user.is_show_homework_modal) {
      const shownAt = localStorage.getItem(HOMEWORK_MODAL_SHOWN_AT);
      const now = Date.now();
      if (!shownAt || now - parseInt(shownAt, 10) > 24 * 60 * 60 * 1000) {
        setOpenModalHomework(true);
        localStorage.setItem(HOMEWORK_MODAL_SHOWN_AT, now.toString());
      }
    }

    if (user.is_show_last_lesson_homework_modal) {
      const shownAt = localStorage.getItem(LAST_LESSON_HOMEWORK_MODAL_SHOWN_AT);
      const now = Date.now();
      if (!shownAt || now - parseInt(shownAt, 10) > 24 * 60 * 60 * 1000) {
        setOpenLastLessonModalHomework(true);
        localStorage.setItem(LAST_LESSON_HOMEWORK_MODAL_SHOWN_AT, now.toString());
      }
    }

    if (isShowLoginAttempt) setOpenModalLoginAttempt(true);
  }, [telegramMessage, birthDateMessage, user, isShowLoginAttempt, telegram]);

  useEffect(() => {
    if (telegram && token) dispatch(setTelegramThunk({ token }));
    if (birthDate && token) dispatch(setBirthDateThunk({ token }));
  }, [telegram, birthDate, token, dispatch]);

  const anyOpen = useMemo(
    () =>
      openModalTelegram ||
      openModalBirth ||
      openModalHomework ||
      openLastLessonModalHomework ||
      openModalLoginAttempt,
    [openModalTelegram, openModalBirth, openModalHomework, openLastLessonModalHomework, openModalLoginAttempt]
  );

  useEffect(() => {
    document.body.classList.toggle('open', anyOpen);
  }, [anyOpen]);

  return (
    <>
      {openModalBirth && (
        <ModalInfo
          notification={withoutImage(birthDateMessage)}
          setOpenModal={setOpenModalBirth}
          openModal={openModalBirth}
          closeOnPrimaryClick={false}
        >
          <div className="modal-info-birth-date">
            <label className="modal-info-birth-date__label">
              Дата народження
            </label>

            <input
              name="birth_date"
              type="date"
              value={birthday}
              onChange={(e) => setBirthday(e.target.value)}
              className="modal-info-birth-date__input"
              required
            />

            {birthDateError && (
              <div className="modal-info-birth-date__error">
                {birthDateError}
              </div>
            )}

            <button
              type="button"
              className="modal-info-birth-date__button"
              onClick={saveBirthDate}
              disabled={birthDateLoading}
            >
              {birthDateLoading ? 'Зберігаємо...' : 'Зберегти'}
            </button>
          </div>
        </ModalInfo>
      )}

      {openModalTelegram && (
        <ModalInfo
          notification={withoutImage(telegramMessage)}
          setOpenModal={setOpenModalTelegram}
          openModal={openModalTelegram}
          closeOnPrimaryClick={false}
        />
      )}

      {openModalHomework && (
        <ModalInfo
          notification={user}
          image={isRedesign ? user.homework_promo_image || '' : ''}
          setOpenModal={setOpenModalHomework}
          openModal={openModalHomework}
          onAccept={setHomeworkPage}
          closeOnPrimaryClick={true}
        />
      )}

      {openLastLessonModalHomework && (
        <ModalInfo
          notification={lastLessonModalHomework}
          image={isRedesign ? user.homework_reminder_image || '' : ''}
          setOpenModal={setOpenLastLessonModalHomework}
          openModal={openLastLessonModalHomework}
          onAccept={setHomeworkPage}
          closeOnPrimaryClick={true}
        />
      )}

      {openModalLoginAttempt && (
        <ModalInfo
          notification={loginAttemptNotification}
          setOpenModal={setOpenModalLoginAttempt}
          openModal={openModalLoginAttempt}
          closeOnPrimaryClick={true}
        />
      )}
    </>
  );
};

export default Modals;
